'use client';

/**
 * PayPal JS SDK v6 一次性支付出站 redirect：
 * - create order 后 session.start({ presentationMode: 'redirect' })
 * - 回跳 confirm 仍由全局 PayPalReturnConfirmHandler 处理（不接 resume / onApprove confirm）
 * - SDK 未就绪或 start 失败时可 fallback 到 checkout_url
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { persistPayReturnUrlsFromCreateOrder } from '@/lib/pay/paypal-return-restore';
import { readArmsFailDetail } from '@/utils/arms/common';
import {
    readPayPalV6ErrorCode,
    reportPayPalV6Degrade,
    reportPayPalV6SdkInit,
    reportPayPalV6SessionError,
} from '@/utils/arms/paypal-events';
import { reportPaypalRedirectDuration } from '@/utils/arms/performance-events';

import type { ICreateOrderData } from '@/apis/commerce';
import type { IPayPalCheckoutArmsFailContext, TPayPalV6CheckoutChannel } from '@/utils/arms/paypal-events';

const SCRIPT_ID = 'paypal-web-sdk-v6-core';

const testClientId = 'ASZ_ARWafGWgDFK4pxwqtMwS_-QH2u25vm0RYmmpgscWrQaF7GxTLCziCgAR9aiu6i2pwNv1v-yQWxrI';
const onlineClientId = 'AeG_aWPr-VdlsH51XNnevwrpka0gvXwdV5Vi4YHZsqr47PFbSTsmkwGn7gUghQWmiePTOHiR6MJlTxG2';

export const PAYPAL_CLIENT_ID = process.env.PAYPAL_CLIENT_ID ?? onlineClientId;

export const PAYPAL_SANDBOX = PAYPAL_CLIENT_ID === testClientId;

const REDIRECT_GUARD_TIMEOUT_MS = 10_000;

const ARMS_SANDBOX_FLAG: 0 | 1 = PAYPAL_SANDBOX ? 1 : 0;

interface IPayPalV6PaymentSession {
    start: (
        presentation: { presentationMode: string; fullPageOverlay?: { enabled: boolean } },
        createOrder: Promise<{ orderId: string }>
    ) => Promise<void>;
}

interface IPayPalV6SdkInstance {
    createPayPalOneTimePaymentSession: (opts: {
        onApprove: (data: { orderId?: string; orderID?: string }) => void | Promise<void>;
        onCancel?: () => void;
        onError?: (err?: unknown) => void;
    }) => IPayPalV6PaymentSession;
}

function getV6ScriptSrc(sandbox: boolean): string {
    return sandbox ? 'https://www.sandbox.paypal.com/web-sdk/v6/core' : 'https://www.paypal.com/web-sdk/v6/core';
}

function loadPayPalV6Script(sandbox: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('no window'));
            return;
        }
        const w = window as Window & {
            paypal?: { createInstance?: (opts: unknown) => Promise<unknown> };
        };
        if (w.paypal?.createInstance) {
            resolve();
            return;
        }
        const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
        if (existing) {
            existing.addEventListener('load', () => resolve(), { once: true });
            existing.addEventListener('error', () => reject(new Error('script error')), { once: true });
            return;
        }
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = getV6ScriptSrc(sandbox);
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load PayPal v6 SDK'));
        document.body.appendChild(script);
    });
}

let sdkInstancePromise: Promise<IPayPalV6SdkInstance | null> | null = null;

function reportSdkInitResult(
    success: 0 | 1,
    initStartTime: number,
    stage?: 'script_load' | 'no_create_instance' | 'create_instance',
    failDetail?: string
): void {
    reportPayPalV6SdkInit({
        success,
        duration_ms: Date.now() - initStartTime,
        sandbox: ARMS_SANDBOX_FLAG,
        ...(stage ? { stage } : {}),
        ...(failDetail ? { fail_detail: failDetail } : {}),
    });
}

export function getPayPalV6SdkInstance(): Promise<IPayPalV6SdkInstance | null> {
    if (!PAYPAL_CLIENT_ID || typeof window === 'undefined') {
        return Promise.resolve(null);
    }
    if (!sdkInstancePromise) {
        const initStartTime = Date.now();
        sdkInstancePromise = (async () => {
            try {
                await loadPayPalV6Script(PAYPAL_SANDBOX);
            } catch (err) {
                reportSdkInitResult(0, initStartTime, 'script_load', readArmsFailDetail(err));
                return null;
            }
            const w = window as Window & {
                paypal?: { createInstance: (opts: unknown) => Promise<unknown> };
            };
            if (!w.paypal?.createInstance) {
                reportSdkInitResult(0, initStartTime, 'no_create_instance', 'paypal.createInstance missing');
                return null;
            }
            try {
                const instance = (await w.paypal.createInstance({
                    clientId: PAYPAL_CLIENT_ID,
                    components: ['paypal-payments'],
                    pageType: 'checkout',
                })) as IPayPalV6SdkInstance;
                reportSdkInitResult(1, initStartTime);
                return instance;
            } catch (err) {
                reportSdkInitResult(0, initStartTime, 'create_instance', readArmsFailDetail(err));
                return null;
            }
        })();
    }
    return sdkInstancePromise;
}

/** 应用启动后预加载 PayPal v6 SDK，失败静默，支付流程会复用同一 promise 或走 checkout_url */
export function preloadPayPalV6Sdk(): void {
    if (!PAYPAL_CLIENT_ID || typeof window === 'undefined') {
        return;
    }
    getPayPalV6SdkInstance().catch(() => {
        // 预加载失败不打扰用户；init 结果已由 getPayPalV6SdkInstance 上报
    });
}

export function pickPayPalOrderIdFromCreateOrderData(data: ICreateOrderData): string | null {
    const fromStore = data.store_order_id?.trim();
    if (fromStore) {
        return fromStore;
    }
    const fromPayload = data.store_payload?.order_id?.trim();
    return fromPayload || null;
}

function redirectToCheckoutUrl(checkoutUrl: string): void {
    window.location.href = checkoutUrl;
}

interface IStartPayPalV6SessionContext {
    commerceOrderId: string;
    paypalOrderId: string;
}

async function startPayPalV6RedirectSession(
    sdk: IPayPalV6SdkInstance,
    ctx: IStartPayPalV6SessionContext
): Promise<void> {
    const { commerceOrderId, paypalOrderId } = ctx;
    const session = sdk.createPayPalOneTimePaymentSession({
        // redirect 回跳由 PayPalReturnConfirmHandler 处理，此处不 confirm
        onApprove: async () => {},
        onCancel: () => {},
        onError: err => {
            reportPayPalV6SessionError({
                stage: 'onError',
                order_id: commerceOrderId,
                paypal_order_id: paypalOrderId,
                error_code: readPayPalV6ErrorCode(err),
                fail_detail: readArmsFailDetail(err),
            });
        },
    });
    try {
        await session.start(
            { presentationMode: 'redirect', fullPageOverlay: { enabled: false } },
            Promise.resolve({ orderId: paypalOrderId })
        );
    } catch (err) {
        reportPayPalV6SessionError({
            stage: 'session_start',
            order_id: commerceOrderId,
            paypal_order_id: paypalOrderId,
            error_code: readPayPalV6ErrorCode(err),
            fail_detail: readArmsFailDetail(err),
        });
        throw err;
    }
}

export interface IStartPayPalV6RedirectParams {
    createCommerceOrder: () => Promise<ICreateOrderData>;
    onGatewayShow?: (orderData: ICreateOrderData) => void;
    onError?: (message: string, armsFail?: IPayPalCheckoutArmsFailContext) => void;
    perfTiming?: {
        clickTime: number;
        book_id: string;
        chapter_id: string;
        gear_id: string;
        goods_type: string;
    };
}

export function usePayPalV6Redirect() {
    const [sdkReady, setSdkReady] = useState(false);
    const [initError, setInitError] = useState<string | null>(null);
    const [paying, setPaying] = useState(false);
    const sdkRef = useRef<IPayPalV6SdkInstance | null>(null);
    const guardTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearGuardTimer = useCallback(() => {
        if (guardTimerRef.current !== null) {
            clearTimeout(guardTimerRef.current);
            guardTimerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (!PAYPAL_CLIENT_ID) {
            return () => {};
        }
        let cancelled = false;
        getPayPalV6SdkInstance()
            .then(instance => {
                if (cancelled) {
                    return;
                }
                if (!instance) {
                    setInitError('PayPal (v6) failed to initialize');
                    return;
                }
                sdkRef.current = instance;
                setSdkReady(true);
            })
            .catch(() => {
                if (!cancelled) {
                    setInitError('PayPal (v6) failed to initialize');
                }
            });
        return () => {
            cancelled = true;
            clearGuardTimer();
        };
    }, [clearGuardTimer]);

    const reportPerfIfNeeded = useCallback(
        (
            params: IStartPayPalV6RedirectParams,
            data: ICreateOrderData,
            apiStartTime: number,
            apiEndTime: number,
            checkoutChannel: TPayPalV6CheckoutChannel
        ) => {
            if (!params.perfTiming) {
                return;
            }
            const preRedirectTime = Date.now();
            const { clickTime, ...ctx } = params.perfTiming;
            reportPaypalRedirectDuration({
                ...ctx,
                page_path: window.location.pathname,
                order_id: data.order_id ?? '',
                total_time: preRedirectTime - clickTime,
                before_fetch_time: apiStartTime - clickTime,
                fetch_time: apiEndTime - apiStartTime,
                after_fetch_time: preRedirectTime - apiEndTime,
                checkout_channel: checkoutChannel,
            });
        },
        []
    );

    const startV6Redirect = useCallback(
        async (params: IStartPayPalV6RedirectParams) => {
            setPaying(true);
            clearGuardTimer();
            guardTimerRef.current = setTimeout(() => {
                guardTimerRef.current = null;
                setPaying(false);
                params.onError?.('Payment gateway timeout, please try again', {
                    fail_reason: 'v6_redirect_guard_timeout',
                });
            }, REDIRECT_GUARD_TIMEOUT_MS);

            const apiStartTime = Date.now();
            let data: ICreateOrderData;
            try {
                data = await params.createCommerceOrder();
            } catch (err) {
                clearGuardTimer();
                setPaying(false);
                params.onError?.('Failed to create order', {
                    fail_reason: 'order_create_api_failed',
                    fail_detail: readArmsFailDetail(err),
                });
                return;
            }
            const apiEndTime = Date.now();

            const checkoutUrl = data.store_payload?.checkout_url?.trim() ?? '';
            const paypalOrderId = pickPayPalOrderIdFromCreateOrderData(data);
            const commerceOrderId = data.order_id ?? '';

            persistPayReturnUrlsFromCreateOrder(data);
            params.onGatewayShow?.(data);

            const sdk = sdkRef.current;
            if (!sdk || !paypalOrderId) {
                if (checkoutUrl) {
                    const checkoutChannel: TPayPalV6CheckoutChannel = !paypalOrderId
                        ? 'v6_fallback_missing_order'
                        : 'v6_fallback_sdk_unavailable';
                    reportPayPalV6Degrade({
                        checkout_channel: checkoutChannel,
                        order_id: commerceOrderId,
                        paypal_order_id: paypalOrderId ?? undefined,
                        page_path: window.location.pathname,
                        fail_detail: !paypalOrderId ? 'missing_paypal_order_id' : 'sdk_not_ready_at_pay',
                    });
                    reportPerfIfNeeded(params, data, apiStartTime, apiEndTime, checkoutChannel);
                    redirectToCheckoutUrl(checkoutUrl);
                    return;
                }
                clearGuardTimer();
                setPaying(false);
                params.onError?.(!paypalOrderId ? 'Missing PayPal order from server' : 'PayPal is not ready', {
                    fail_reason: 'order_create_missing_checkout_url',
                    order_id: commerceOrderId,
                    fail_detail: !paypalOrderId ? 'missing_paypal_order_id' : 'sdk_not_ready_at_pay',
                });
                return;
            }

            try {
                await startPayPalV6RedirectSession(sdk, { commerceOrderId, paypalOrderId });
                reportPerfIfNeeded(params, data, apiStartTime, apiEndTime, 'v6_redirect');
            } catch (err) {
                if (checkoutUrl) {
                    reportPayPalV6Degrade({
                        checkout_channel: 'v6_fallback_session_start',
                        order_id: commerceOrderId,
                        paypal_order_id: paypalOrderId,
                        page_path: window.location.pathname,
                        fail_detail: readArmsFailDetail(err),
                    });
                    reportPerfIfNeeded(params, data, apiStartTime, apiEndTime, 'v6_fallback_session_start');
                    redirectToCheckoutUrl(checkoutUrl);
                    return;
                }
                clearGuardTimer();
                setPaying(false);
                params.onError?.('Could not start PayPal checkout', {
                    fail_reason: 'v6_session_start_failed',
                    order_id: commerceOrderId,
                    fail_detail: readArmsFailDetail(err),
                });
            }
        },
        [clearGuardTimer, reportPerfIfNeeded]
    );

    return {
        sdkReady: Boolean(PAYPAL_CLIENT_ID) && sdkReady,
        initError,
        paying,
        startV6Redirect,
    };
}
